import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Accordion, Card, useAccordionButton } from 'react-bootstrap';

import { CheckBlock } from 'components';
import IntegrationsHeader from 'pages/Hardware/Integrations/IntegrationsHeader';
import MicroserviceInstallation from 'pages/Hardware/Installer/MicroserviceInstallation';
import {
  getBulkConfigurations,
  saveConfiguration,
  updateConfiguration,
} from 'service/ConfigAPI';
import GoMSForm from 'pages/Hardware/Integrations/Types/printer/GoMSPrinting';
import { Ctx } from 'components/CtxFormFields/CtxInputs';
import { addError } from 'rdx/alert';

function Header ({eventKey, children}) {
  const onClick = useAccordionButton(eventKey, () => console.log(`opened ${eventKey}`));

  return (
    <Card.Header className="pointer" onClick={onClick}>
      {children}
    </Card.Header>
  )
}
// TODO: implement the fields for goMS based on required/optional fields from the condigSample

function Printer() {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { level, level_id } = useParams();
  const [cafaConfigs, setCafaConfigs] = useState({});
  const [printingSettings, setPrintingSettings] = useState({});

  const updatePrintingConfig = (key, value) => {
    setPrintingSettings({
      ...printingSettings,
      [key]: { ...printingSettings[key], ...value },
    });
  };

  useEffect(() => {
    getBulkConfigurations({ level, level_id, type: 'printer' })
      .then(({ data }) => {
        const configs = {};
        const configValues = {};
        data.forEach((c) => {
          configs[c.name] = c;
          configValues[c.name] = c.value;
        });
        setCafaConfigs(configs);
        setPrintingSettings(configValues);
      })
      .catch((err) => console.error(err));
  }, []);

  // updates/toggles the enabled value in the configuration
  const togglePrintingOption = (e) => {
    const { name, checked } = e.target;
    const integration = cafaConfigs[name];

    const levelIDIsMatching =
      integration?.level === 'Company'
        ? true
        : integration?.level_id === level_id;
    const save =
      integration?.id && integration?.level === level && levelIDIsMatching
        ? updateConfiguration
        : saveConfiguration;

    save({
      level,
      level_id,
      name,
      type: 'printer',
      value: { ...printingSettings[name], enabled: checked ? 1 : 0 },
    })
      .then(() =>
        setPrintingSettings({
          ...printingSettings,
          [name]: { ...printingSettings[name], enabled: checked },
        }),
      )
      .catch((error) => {
        console.error(error);
        dispatch(addError('Updating configuration failed.'));
      });
  };

  // updates the configuration of the printing option
  const saveConfigToCAFA = async (name) => {
    const integration = cafaConfigs[name];

    const levelIDIsMatching =
      integration?.level === 'Company'
        ? true
        : integration?.level_id === level_id;

    const save =
      integration?.id && integration?.level === level && levelIDIsMatching
        ? updateConfiguration
        : saveConfiguration;

    await save({
      level,
      level_id,
      name,
      type: 'printer',
      value: printingSettings[name],
    }).catch((e) => {
      console.error(e);
      dispatch(addError('Updating configuration failed.'));
    });
  };

  const resetConfigState = (name) => {
    const checkedStatus = printingSettings[name].enabled;
    setPrintingSettings({
      ...printingSettings,
      [name]: {
        ...cafaConfigs[name].value,
        enabled: checkedStatus,
      },
    });
  };

  return (
    <Ctx.Provider
      value={useMemo(
        () => ({
          values: printingSettings,
          levelData: { level, level_id },
          onChange: updatePrintingConfig,
        }),
        [printingSettings, level, level_id, updatePrintingConfig],
      )}
    >
      <div className="d-flex flex-fill flex-column">
        <IntegrationsHeader
          goToPath="/hardware/integrations"
          level={level}
          title="Printer"
        />
        <Accordion defaultActiveKey="">
          <Card>
            <Header eventKey="wrapper">
              <div className="d-flex justify-content-between">
                <span>Wrapper printing</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <CheckBlock
                    type="switch"
                    name="wrapper"
                    onClick={togglePrintingOption}
                    checked={!!printingSettings?.wrapper?.enabled}
                  />
                </div>
              </div>
            </Header>
            <Accordion.Collapse eventKey="wrapper">
              <Card.Body>
                {`Mobile applications for POS Brazil ("Wrappers") have build-in printing
          functionality.`}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card className="mt-3">
            <Header eventKey="goMS">
              <div className="d-flex justify-content-between">
                <span>Golang printing micro-service</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <CheckBlock
                    type="switch"
                    name="goMS"
                    onClick={togglePrintingOption}
                    checked={!!printingSettings?.goMS?.enabled}
                  />
                </div>
              </div>
            </Header>
            <Accordion.Collapse eventKey="goMS">
              <Card.Body>
                Golang printing micro-service is a printing option that would
                require the installation of the{' '}
                <code>printing-microservice</code>. The micro-service would
                allow POS Brazil to connect to numerous printing devices(link to
                supported devices coming soon) and print.
                <div className="mt-2">
                  <MicroserviceInstallation microService="printing-microservice" />
                  <div className="d-flex justify-content-end mt-1">
                    <Link to="/hardware/installer/msconfig/printing-microservice">
                      Go to micro-service manager
                    </Link>
                  </div>
                </div>
                <GoMSForm
                  saveConfigToCAFA={saveConfigToCAFA}
                  resetConfigState={resetConfigState}
                />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card className="mt-3">
            <Header eventKey="epsi">
              <div className="d-flex justify-content-between">
                <span>EPSI printing</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <CheckBlock
                    type="switch"
                    name="epsi"
                    onClick={togglePrintingOption}
                    checked={!!printingSettings?.epsi?.enabled}
                  />
                </div>
              </div>
            </Header>
            <Accordion.Collapse eventKey="epsi">
              <Card.Body>
                <p>
                  EPLSI is the legacy printing option from POS Berlin. It is
                  implemented in POS Brazil as well for backwards compatibility
                  but we strongly recommend the usage of the Golang printing
                  micro-service.
                </p>
                <p>
                  In order to be able to use EPSI you would need to run it
                  locally on your machine. More information about the process
                  can be found in{' '}
                  <a
                    href="https://abi.erply.com/kassa/pos-berlin-juhend/6-seaded/vaelised-seadmed-epsi/paigaldamisjuhend/allalaadimine"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    this manual
                  </a>
                  .
                </p>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card className="mt-3">
            <Header eventKey="browser">
              <div className="d-flex justify-content-between">
                <span>Browser built-in printing</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <CheckBlock
                    type="switch"
                    name="browser"
                    onClick={togglePrintingOption}
                    checked={!!printingSettings?.browser?.enabled}
                  />
                </div>
              </div>
            </Header>
            <Accordion.Collapse eventKey="browser">
              <Card.Body>
                Browser printing is a fallback option in case none of the above
                is available. It uses the Browser API and templates generated by
                POS Brazil.
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
    </Ctx.Provider>
  );
}

export default {
  linkName: 'Printer',
  path: '/hardware/integrations/:level/:level_id/printer',
  hidden: true,
  component: Printer,
  subRoutes: [],
  rootPath: { name: 'Hardware', uri: '/hardware' },
  subPath: [
    { name: 'Integrations', uri: '/hardware/integrations' },
    { name: 'Printing' },
  ],
};
